'use client';

import { createClient } from '@/lib/supabase/client';

export interface MovementRegistrationData {
  fullName: string;
  email: string;
  country?: string;
  motivation?: string;
  sourcePage: string;
}

export interface AnalyticsStats {
  totalMembers: number;
  visitorsToday: number;
  visitorsThisWeek: number;
  visitorsThisMonth: number;
  visitorsThisYear: number;
}

export const movementRegistrationService = {
  async register(data: MovementRegistrationData): Promise<{ success: boolean; error?: string }> {
    const supabase = createClient();
    try {
      const { error } = await supabase.from('movement_registrations').insert({
        full_name: data.fullName,
        email: data.email,
        country: data.country || null,
        motivation: data.motivation || null,
        source_page: data.sourcePage,
        registered_at: new Date().toISOString(),
      });

      if (error) {
        if (error.code === '23505') {
          return { success: false, error: 'This email is already registered.' };
        }
        return { success: false, error: error.message };
      }
      return { success: true };
    } catch (err: any) {
      return { success: false, error: err.message };
    }
  },

  async trackVisit(page: string = 'homepage'): Promise<void> {
    const supabase = createClient();
    try {
      const sessionId = typeof window !== 'undefined' ? (sessionStorage.getItem('sig_session_id') || (() => {
            const id = Math.random().toString(36).substring(2);
            sessionStorage.setItem('sig_session_id', id);
            return id;
          })())
        : null;

      const hostname = typeof window !== 'undefined' ? window.location.hostname : null;

      await supabase.from('visitor_analytics').insert({
        visited_at: new Date().toISOString(),
        page,
        session_id: sessionId,
        hostname,
      });
    } catch {
      // Silent fail for analytics
    }
  },

  async getAnalyticsStats(): Promise<AnalyticsStats> {
    const supabase = createClient();
    const now = new Date();

    const startOfDay = new Date(now);
    startOfDay.setHours(0, 0, 0, 0);

    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);

    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const startOfYear = new Date(now.getFullYear(), 0, 1);

    // Always use production hostname for consistent analytics across all environments
    const PRODUCTION_HOSTNAME = 'noenemy.life';

    try {
      const [membersResult, todayResult, weekResult, monthResult, yearResult] = await Promise.all([
        supabase.from('movement_registrations').select('id', { count: 'exact', head: true }),
        supabase
          .from('visitor_analytics')
          .select('id', { count: 'exact', head: true })
          .eq('hostname', PRODUCTION_HOSTNAME)
          .gte('visited_at', startOfDay.toISOString()),
        supabase
          .from('visitor_analytics')
          .select('id', { count: 'exact', head: true })
          .eq('hostname', PRODUCTION_HOSTNAME)
          .gte('visited_at', startOfWeek.toISOString()),
        supabase
          .from('visitor_analytics')
          .select('id', { count: 'exact', head: true })
          .eq('hostname', PRODUCTION_HOSTNAME)
          .gte('visited_at', startOfMonth.toISOString()),
        supabase
          .from('visitor_analytics')
          .select('id', { count: 'exact', head: true })
          .eq('hostname', PRODUCTION_HOSTNAME)
          .gte('visited_at', startOfYear.toISOString()),
      ]);

      return {
        totalMembers: membersResult.count || 0,
        visitorsToday: todayResult.count || 0,
        visitorsThisWeek: weekResult.count || 0,
        visitorsThisMonth: monthResult.count || 0,
        visitorsThisYear: yearResult.count || 0,
      };
    } catch {
      return {
        totalMembers: 0,
        visitorsToday: 0,
        visitorsThisWeek: 0,
        visitorsThisMonth: 0,
        visitorsThisYear: 0,
      };
    }
  },
};
